import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { AvatarComponent } from '../avatar/avatar.component';
import { RouterLink } from '@angular/router';
import { SetOptional } from '@pool-league/shared-util-misc';
import { PlayerDetails } from './player-details';
import { PLAYER_COMPONENT_CONFIG } from './config';

@Component({
  selector: 'lib-player',
  imports: [CommonModule, AvatarComponent, RouterLink],
  templateUrl: './player.component.html',
  styleUrl: './player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.no-avatar]': '!showAvatar()',
  },
})
export class PlayerComponent {
  readonly #config = inject(PLAYER_COMPONENT_CONFIG);
  readonly player = input.required<
    string | SetOptional<PlayerDetails, 'bbUserName'>
  >();
  readonly linkEnabled = input(true);
  readonly darkText = input(false);
  readonly trailingAvatar = input(false);
  readonly showAvatar = input(true);
  readonly avatarSize = input<keyof typeof AvatarComponent.SIZE>('xs');
  readonly rosette = input<string | undefined>();
  readonly #player$ = toObservable(this.player).pipe(
    switchMap((item) => {
      if (typeof item === 'string') {
        return item ? this.#config.detailsResolver.get(item) : of(undefined);
      }
      return hasBbUserName(item)
        ? of(item)
        : item.id
          ? this.#config.detailsResolver.get(item.id)
          : of(undefined);
    }),
  );
  readonly playerDetails = toSignal(this.#player$);
  readonly link = computed(() => {
    const bbUserName = this.playerDetails()?.bbUserName;
    return bbUserName && this.linkEnabled()
      ? this.#config.profileLink(bbUserName)
      : undefined;
  });
}

function hasBbUserName(
  item: SetOptional<PlayerDetails, 'bbUserName'>,
): item is PlayerDetails {
  return !!item.bbUserName;
}
